import { RadioGroup } from "@outschool/backpack";
import { Search as SearchFilters } from "@outschool/business-rules";
import { SearchFilters as SearchFiltersType } from "@outschool/gql-backend-generated";
import { useTranslation } from "@outschool/localization";
import {
  Filter,
  FilterDropdown,
  FilterList,
} from "@outschool/ui-components-website";
import React from "react";

import { PriceFilterProps } from "./PriceFilter";

type CreditFilterProps = Omit<
  PriceFilterProps,
  "selectedSearchToggle" | "onCancel" | "onChange" | "priceFilterValue"
> & { creditFilterValue: SearchFiltersType; isCourse: boolean };

export const useCreditFilterOptions = (isCourse: boolean = false) => {
  const { t } = useTranslation("client\\components\\search\\CreditFilter");
  const { options, filterKeys } = isCourse
    ? {
        options: SearchFilters.CREDIT_PRICE_PER_COURSE_OPTIONS,
        filterKeys: {
          minValueKey: "priceCreditsPerCourseMin",
          maxValueKey: "priceCreditsPerCourseMax",
        },
      }
    : {
        options: SearchFilters.CREDIT_PRICE_PER_MEETING_OPTIONS,
        filterKeys: {
          minValueKey: "priceCreditsPerMeetingMin",
          maxValueKey: "priceCreditsPerMeetingMax",
        },
      };
  return options.map((option: Record<string, number>) => {
    const [priceCreditsMin, priceCreditsMax] = [
      option[filterKeys.minValueKey],
      option[filterKeys.maxValueKey],
    ];
    return !priceCreditsMax && !priceCreditsMin
      ? {
          label: t("Any"),
          value: JSON.stringify({
            [filterKeys.minValueKey]: null,
            [filterKeys.maxValueKey]: null,
          }),
        }
      : priceCreditsMax
      ? {
          label: t("{{creditMax}} credits or less", {
            creditMax: priceCreditsMax,
          }),
          value: JSON.stringify({
            [filterKeys.minValueKey]: null,
            [filterKeys.maxValueKey]: priceCreditsMax,
          }),
        }
      : {
          label: t("Over {{creditMin}} credits", {
            creditMin: priceCreditsMin,
          }),
          value: JSON.stringify({
            [filterKeys.minValueKey]: priceCreditsMin,
            [filterKeys.maxValueKey]: null,
          }),
        };
  });
};

type SearchFiltersTypeKey = keyof SearchFiltersType;

export default function CreditFilter({
  open,
  handleChangesExternally,
  handleImplicitFilterChange,
  onClose,
  onOpen,
  element,
  buttonSx,
  creditFilterValue,
  isCourse = false,
}: CreditFilterProps) {
  const { t } = useTranslation("client\\components\\search\\CreditFilter");
  const priceCreditsOptions = useCreditFilterOptions(isCourse);

  const [minValueKey, maxValueKey]: [
    SearchFiltersTypeKey,
    SearchFiltersTypeKey,
  ] = isCourse
    ? ["priceCreditsPerCourseMin", "priceCreditsPerCourseMax"]
    : ["priceCreditsPerMeetingMin", "priceCreditsPerMeetingMax"];
  const [creditMax, creditMin] = [
    creditFilterValue[maxValueKey],
    creditFilterValue[minValueKey],
  ];

  const selectedCreditOption =
    creditMax || creditMin
      ? priceCreditsOptions.find(option => {
          const optionValue = JSON.parse(option.value);
          const [priceCreditsMax, priceCreditsMin] = [
            optionValue[maxValueKey],
            optionValue[minValueKey],
          ];
          if (priceCreditsMax) {
            return priceCreditsMax === creditMax;
          }
          if (priceCreditsMin) {
            return priceCreditsMin === creditMin;
          }
          return false;
        })
      : undefined;

  const handleFilterChange = (
    _e: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) => {
    const parsedValue = JSON.parse(value);
    const newFilterValue = {
      pricePerMeetingMax: null,
      pricePerMeetingMin: null,
      pricePerCourseMax: null,
      pricePerCourseMin: null,
      priceCreditsPerMeetingMax: null,
      priceCreditsPerMeetingMin: null,
      priceCreditsPerCourseMax: null,
      priceCreditsPerCourseMin: null,
      ...parsedValue,
    };
    if (handleChangesExternally) {
      handleImplicitFilterChange?.("priceCredits", newFilterValue);
    }
  };

  return (
    <FilterDropdown
      filterName={t("Cost")}
      filterValue={
        selectedCreditOption ? selectedCreditOption.label : undefined
      }
      referenceElement={element}
      buttonSx={buttonSx}
      open={open}
      onClose={onClose}
      onOpen={onOpen}
    >
      <FilterList>
        <Filter title={isCourse ? t("Cost per course") : t("Cost per class")}>
          <RadioGroup
            name="price"
            value={
              selectedCreditOption
                ? selectedCreditOption.value
                : JSON.stringify({
                    [minValueKey]: null,
                    [maxValueKey]: null,
                  })
            }
            options={priceCreditsOptions}
            onChange={handleFilterChange}
          />
        </Filter>
      </FilterList>
    </FilterDropdown>
  );
}
